import { observer } from 'mobx-react-lite';
import AttachmentWellBarView from './AttachmentWellBarView';
import getAllowShowMultipleRows from './Helpers/getAllowShowMultipleRows';
import { logUsage } from 'owa-analytics';
import { AttachmentFile } from 'owa-attachment-file-types';
import { AttachmentDragData } from 'owa-attachment-drag-and-drop';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentView } from 'owa-attachment-full-view';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import { deleteAttachmentViaQueueManager } from 'owa-attachment-well-data/lib/actions/createAttachmentsViaQueueManager';
import setCanOverrideAllowShowMultipleRows from 'owa-attachment-well-data/lib/actions/setCanOverrideAllowShowMultipleRows';
import setDidViewOverflowOneRow from 'owa-attachment-well-data/lib/actions/setDidViewOverflowOneRow';
import AttachmentWellViewState from 'owa-attachment-well-data/lib/schema/AttachmentWellViewState';
import { OnAttachmentDeletedCallback } from 'owa-attachment-well-data/lib/types/AttachmentDeleteQueueItem';
import createAttachmentFileFromExistingMailboxAttachment from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/createAttachmentFileFromExistingMailboxAttachment';
import replaceAttachment from 'owa-attachment-well-data/lib/utils/replaceAttachment';
import { createImageGalleryForNewSxS } from 'owa-attachment-well-preview-actions';
import { ClientItemId, ClientAttachmentId } from 'owa-client-ids';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import shouldShowUnstackedReadingPane from 'owa-mail-store/lib/utils/shouldShowUnstackedReadingPane';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { deleteSmimeAttachmentViaSmimeQueueManager } from 'owa-smime/lib/utils/SmimeAttachmentQueueManager/createSmimeAttachmentViaSmimeQueueManager';
import {
    isSxSDisplayedForAttachment,
    lazyForceCloseSxS,
    getOrCreateSxSStoreData,
    getActiveSxSId,
} from 'owa-sxs-store';
import getAttachmentPreviewMode from 'owa-sxs-store/lib/utils/getAttachmentPreviewMode';
import * as React from 'react';
import { lazyPrefetchWacResourcesForAttachments } from 'owa-wac-prefetch-resources';
import { AriaRoles } from 'owa-accessibility';
import { AttachmentWellAriaLabelText } from './AttachmentWellView.locstring.json';
import loc from 'owa-localize';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';

export interface AttachmentWellProps {
    attachmentWellViewState: AttachmentWellViewState;
    parentItemId: ClientItemId; // We decide to render attachment well even when the draft has not been saved for the first time because we want to handle sharing tips for links in the body during this time.
    isDraft: boolean;
    instrumentationContext?: InstrumentationContext;
    infoBar?: InfoBarHostViewState;
    onAttachmentDeletedCallback?: OnAttachmentDeletedCallback;
    moveAttachmentToInlineHandler?: (
        attachment: AttachmentFullViewState,
        contentId: string
    ) => Promise<void>;
    onLostFocus?: () => void;
    referenceAttachmentsSupported?: () => boolean;
    isDeletionDisabled?: boolean;
}

const styles = require('./AttachmentWellView.scss');
const classNames = require('classnames/bind').bind(styles);
const CHECK_VIEW_OVERFLOW_RETRY_MAX_TIMES = 3;

// This component is responsible for showing the attachment Well view.
export default observer(function AttachmentWellView(props: AttachmentWellProps) {
    const targetWindow = React.useContext(ProjectionContext);
    const sxsId = getActiveSxSId(targetWindow);
    let isViewUnmounted: boolean = false;
    let allowedCheckViewOverflowRetryTimes: number = CHECK_VIEW_OVERFLOW_RETRY_MAX_TIMES;
    React.useEffect(() => {
        // Delayed setTimeout is used in this case to ensure that other datapoints are finished.
        setTimeout(() => {
            const docAttachmentIds = (props.attachmentWellViewState.docViewAttachments || []).map(
                docAttachment => docAttachment.attachmentId
            );
            lazyPrefetchWacResourcesForAttachments.importAndExecute(docAttachmentIds);
        }, 2000);

        lazySubscribeToResizeEvent.importAndExecute(resetAndCheckWhetherViewOverflowOneRow);
        return () => {
            isViewUnmounted = true;
            lazyUnsubscribeFromResizeEvent.importAndExecute(resetAndCheckWhetherViewOverflowOneRow);
        };
    }, []);
    React.useEffect(
        () => {
            checkWhetherViewOverflowOneRow();
        }
        // No dependency array, to ensure the effect is run every render
    );
    const imageAttachmentWellDiv = React.useRef<HTMLDivElement>();
    const docAttachmentWellDiv = React.useRef<HTMLDivElement>();
    const focusFunctions = React.useRef({});
    const onDeleteAttachment = (attachmentViewState: AttachmentFullViewState) => {
        const {
            attachmentWellViewState,
            parentItemId,
            infoBar,
            onAttachmentDeletedCallback,
            isDeletionDisabled,
        } = props;
        // Using slice to get a copy of the arrays
        const images: AttachmentFullViewState[] = attachmentWellViewState.imageViewAttachments.slice(
            0
        );
        const docs: AttachmentFullViewState[] = attachmentWellViewState.docViewAttachments.slice(0);

        if (isDeletionDisabled) {
            return;
        }

        if (isSmimeAttachment(attachmentViewState.attachmentId.Id)) {
            deleteSmimeAttachmentViaSmimeQueueManager(
                attachmentWellViewState,
                attachmentViewState,
                parentItemId,
                infoBar,
                onAttachmentDeletedCallback
            );
        } else {
            const onAttachmentDeletedCallbackHelper = (
                parentItemId: ClientItemId,
                attachmentId: ClientAttachmentId,
                attachment: AttachmentType
            ) => {
                onAttachmentDeletedCallback(parentItemId, attachmentId, attachment);
                /*
                This is to "pause" the following execution to let the Announce component rendering process above catch up.
                Otherwise, it will sometimes set the focus on the next attachment first and then render the Annouce component,
                in which case Narrator will read the next attachment first, instead of the success alert first.
                */
                setTimeout(() => {
                    previewNextAttachmentOrClosePreview(attachmentViewState, images, docs);
                }, 0);
            };
            deleteAttachmentViaQueueManager(
                attachmentWellViewState,
                attachmentViewState,
                parentItemId,
                infoBar,
                onAttachmentDeletedCallbackHelper
            );
        }
    };
    const replaceAttachmentHandler = (
        attachmentViewState: AttachmentFullViewState,
        attachmentType: AttachmentType,
        isReadOnly: boolean,
        uploadCompleted: boolean
    ) => {
        replaceAttachment(
            props.attachmentWellViewState,
            attachmentViewState,
            attachmentType,
            isReadOnly,
            uploadCompleted
        );
    };
    const previewNextAttachmentOrClosePreview = async (
        attachmentViewState: AttachmentFullViewState,
        images: AttachmentFullViewState[],
        docs: AttachmentFullViewState[]
    ) => {
        const imageIndex = images.indexOf(attachmentViewState);
        const docIndex = docs.indexOf(attachmentViewState);
        if (imageIndex > -1) {
            images.splice(imageIndex, 1);
        }
        if (docIndex > -1) {
            docs.splice(docIndex, 1);
        }
        const nextAttachmentViewState = getNextAttachmentViewState(
            images,
            docs,
            imageIndex,
            docIndex
        );
        if (isSxSDisplayedForAttachment(attachmentViewState.attachmentId, sxsId)) {
            const forceCloseSxS = await lazyForceCloseSxS.import();
            forceCloseSxS('deleteAttachment', sxsId);
        }
        // set focus to next attachment after deleting one, if it is the last attachment, set focus to the message body
        if (
            nextAttachmentViewState?.attachmentId?.Id &&
            focusFunctions.current[nextAttachmentViewState.attachmentId.Id]
        ) {
            focusFunctions.current[nextAttachmentViewState.attachmentId.Id]();
        } else {
            if (props.onLostFocus) {
                props.onLostFocus();
            }
        }
    };
    const getImageAttachmentWellDiv = (c: HTMLDivElement) => {
        imageAttachmentWellDiv.current = c;
    };
    const getDocAttachmentWellDiv = (c: HTMLDivElement) => {
        docAttachmentWellDiv.current = c;
    };
    const onBeforePreviewAttachment = () => {
        setCanOverrideAllowShowMultipleRows(props.attachmentWellViewState, true);
    };
    const createImageGallery = async () => {
        return createImageGalleryForNewSxS(props.attachmentWellViewState);
    };
    const getAllowShowMultipleRows_0 = (): boolean => {
        return getAllowShowMultipleRows(
            props.attachmentWellViewState,
            getOrCreateSxSStoreData(sxsId).extendedViewState.attachmentId
        );
    };

    const resetAndCheckWhetherViewOverflowOneRow = () => {
        allowedCheckViewOverflowRetryTimes = CHECK_VIEW_OVERFLOW_RETRY_MAX_TIMES;
        checkWhetherViewOverflowOneRow();
    };

    const checkWhetherViewOverflowOneRow = () => {
        setTimeout(() => {
            if (!isViewUnmounted) {
                internalCheckWhetherViewOverflowOneRow();
            } else {
                logUsage('CheckWhetherWellViewOverflowOneRowWhenUnmouted', {
                    allowedRetryTimes: allowedCheckViewOverflowRetryTimes,
                });
            }
        }, 1 + 1000 * (CHECK_VIEW_OVERFLOW_RETRY_MAX_TIMES - allowedCheckViewOverflowRetryTimes));
    };

    const internalCheckWhetherViewOverflowOneRow = () => {
        let needToRetry: boolean = false;
        if (props.attachmentWellViewState.imageViewAttachments.length > 0) {
            if (isElementInfoReady(imageAttachmentWellDiv.current)) {
                if (didViewOverflowOneRow(imageAttachmentWellDiv.current)) {
                    setDidViewOverflowOneRow(props.attachmentWellViewState, true);
                    return;
                }
            } else {
                needToRetry = true;
            }
        }
        if (props.attachmentWellViewState.docViewAttachments.length > 0) {
            if (isElementInfoReady(docAttachmentWellDiv.current)) {
                if (didViewOverflowOneRow(docAttachmentWellDiv.current)) {
                    setDidViewOverflowOneRow(props.attachmentWellViewState, true);
                    return;
                } else if (!needToRetry) {
                    setDidViewOverflowOneRow(props.attachmentWellViewState, false);
                }
            } else {
                needToRetry = true;
            }
        }

        if (needToRetry) {
            if (--allowedCheckViewOverflowRetryTimes >= 0) {
                logUsage('AttachmentWellRetryToCheckWhetherViewOverflowOneRow');
                checkWhetherViewOverflowOneRow();
            } else {
                logUsage('AttachmentWellRetryToCheckWhetherViewOverflowOneRowFailed');
            }
        }
    };
    const registerFocusFunction = (attachmentId: string, focusFunction: () => void) => {
        focusFunctions.current[attachmentId] = focusFunction;
    };
    const { attachmentWellViewState } = props;
    const conditionalWellStyles = {
        attachmentWellInCompose: !props.attachmentWellViewState.isReadOnly,
    };
    if (
        props.attachmentWellViewState.imageViewAttachments.length +
            props.attachmentWellViewState.docViewAttachments.length ===
        0
    ) {
        return null;
    } else {
        const conditionalStyles = {
            allowShowMultipleRows: getAllowShowMultipleRows_0(),
        };
        const imageViewConditionalStyles = {
            oneRowViewImageHeight: !getAllowShowMultipleRows_0(),
        };
        const docViewConditionalStyles = {
            oneRowViewDocHeight: !getAllowShowMultipleRows_0(),
        };
        return (
            <div
                tabIndex={-1}
                className={classNames([
                    styles.attachmentWell,
                    conditionalWellStyles,
                    'disableTextSelection',
                    shouldShowUnstackedReadingPane() && styles.unstackedReadingPane,
                ])}
                onClick={onClickAttachmentWell}>
                {attachmentWellViewState.imageViewAttachments.length > 0 ? (
                    <div
                        role={AriaRoles.listbox}
                        aria-label={loc(AttachmentWellAriaLabelText)}
                        className={classNames(
                            styles.oneRowView,
                            conditionalStyles,
                            imageViewConditionalStyles
                        )}
                        ref={getImageAttachmentWellDiv}>
                        {attachmentWellViewState.imageViewAttachments.map(attachmentViewState => (
                            <AttachmentView
                                key={attachmentViewState.attachmentId.Id}
                                attachmentViewState={attachmentViewState}
                                onDeleteButtonClicked={onDeleteAttachment}
                                onReplaceAttachment={replaceAttachmentHandler}
                                onDragStart={getAttachmentDragData}
                                instrumentationContext={props.instrumentationContext}
                                onBeforePreview={onBeforePreviewAttachment}
                                isImage={true}
                                parentItemId={props.parentItemId}
                                isDraft={props.isDraft}
                                previewMode={getAttachmentPreviewMode(
                                    attachmentViewState.attachmentId,
                                    attachmentWellViewState.composeId,
                                    attachmentWellViewState.isInitialized,
                                    attachmentWellViewState.isReadOnly,
                                    !!attachmentWellViewState.groupId,
                                    sxsId
                                )}
                                moveAttachmentToInlineHandler={props.moveAttachmentToInlineHandler}
                                createImageGallery={createImageGallery}
                                registerFocusFunction={registerFocusFunction}
                            />
                        ))}
                    </div>
                ) : null}
                {attachmentWellViewState.docViewAttachments.length > 0 ? (
                    <div
                        role={AriaRoles.listbox}
                        aria-label={loc(AttachmentWellAriaLabelText)}
                        className={classNames(
                            styles.oneRowView,
                            conditionalStyles,
                            docViewConditionalStyles
                        )}
                        ref={getDocAttachmentWellDiv}>
                        {props.attachmentWellViewState.docViewAttachments.map(
                            attachmentViewState => (
                                <AttachmentView
                                    key={attachmentViewState.attachmentId.Id}
                                    attachmentViewState={attachmentViewState}
                                    onDeleteButtonClicked={onDeleteAttachment}
                                    onReplaceAttachment={replaceAttachmentHandler}
                                    onDragStart={getAttachmentDragData}
                                    instrumentationContext={props.instrumentationContext}
                                    onBeforePreview={onBeforePreviewAttachment}
                                    isImage={false}
                                    parentItemId={props.parentItemId}
                                    isDraft={props.isDraft}
                                    previewMode={getAttachmentPreviewMode(
                                        attachmentViewState.attachmentId,
                                        attachmentWellViewState.composeId,
                                        attachmentWellViewState.isInitialized,
                                        attachmentWellViewState.isReadOnly,
                                        !!attachmentWellViewState.groupId,
                                        sxsId
                                    )}
                                    createImageGallery={createImageGallery}
                                    registerFocusFunction={registerFocusFunction}
                                    referenceAttachmentsSupported={
                                        props.referenceAttachmentsSupported
                                    }
                                />
                            )
                        )}
                    </div>
                ) : null}
                {attachmentWellViewState.isReadOnly &&
                attachmentWellViewState.imageViewAttachments.length +
                    attachmentWellViewState.docViewAttachments.length >
                    0 ? (
                    <AttachmentWellBarView
                        attachmentWellViewState={attachmentWellViewState}
                        parentItemId={props.parentItemId}
                        isDraft={props.isDraft}
                        attachmentIdBeingPreviewed={
                            getOrCreateSxSStoreData(sxsId).extendedViewState.attachmentId
                        }
                        sxsId={sxsId}
                    />
                ) : null}
            </div>
        );
    }
});

function getNextAttachmentViewState(
    images: AttachmentFullViewState[],
    docs: AttachmentFullViewState[],
    imageIndex: number,
    docIndex: number
): AttachmentFullViewState {
    if (imageIndex > -1) {
        if (images.length === 0) {
            return docs[0];
        }
        if (imageIndex < images.length) {
            return images[imageIndex];
        }
        return images[imageIndex - 1];
    } else {
        if (docs.length === 0) {
            return images[images.length - 1];
        }
        if (docIndex < docs.length) {
            return docs[docIndex];
        }
        return docs[docIndex - 1];
    }
}

function onClickAttachmentWell(ev: any) {
    ev.stopPropagation();
}

function didViewOverflowOneRow(c: HTMLDivElement): boolean {
    return !!c && c.children.length > 0 && c.scrollHeight > 2 * c.children[0].clientHeight;
}

function isElementInfoReady(c: HTMLDivElement): boolean {
    return !!c && c.children.length > 0;
}

function getAttachmentDragData(attachment: AttachmentType): AttachmentDragData {
    const attachmentFile: AttachmentFile = createAttachmentFileFromExistingMailboxAttachment(
        attachment
    );
    const attachmentDragData: AttachmentDragData = {
        itemType: DraggableItemTypes.Attachment,
        attachmentFile: attachmentFile,
    };
    return attachmentDragData;
}
